// Third party
import React from 'react';

// Components
import { UploadImageIcon } from '.';
import { UploadPhotoModal, UploadLogoModal } from '../modals';
import { DestructiveModal } from '@capintel/component-lib/destructive-modal';
import { Button } from '../buttons';

// Constants and types
import User from '@/types/user';
import EditableUserInfos from '@/types/editable-user-infos';

// Utils
import notification from '../../../utils/notification';
import { formatMessage, FormattedMessage } from '../../../utils/i18n';
import UserForm from './UserForm';
import { PERMISSIONS, userHasPermission } from '@/utils/permissions';

type Props = {
  user: User;
  onSave: (info: EditableUserInfos) => Promise<User>;
};

type State = {
  showUploadPhotoModal: boolean;
  showDeletePhotoModal: boolean;
  showUploadLogoModal: boolean;
  showDeleteLogoModal: boolean;
};

class UserOnboarding extends React.Component<Props, State> {
  readonly state: State = {
    showUploadPhotoModal: false,
    showDeletePhotoModal: false,
    showUploadLogoModal: false,
    showDeleteLogoModal: false,
  };

  hideUploadPhotoModal = () => {
    this.setState({ showUploadPhotoModal: false });
  };

  hideUploadLogoModal = () => {
    this.setState({ showUploadLogoModal: false });
  };

  hideDeletePhotoModal = () => {
    this.setState({ showDeletePhotoModal: false });
  };

  hideDeleteLogoModal = () => {
    this.setState({ showDeleteLogoModal: false });
  };

  showUploadPhotoModal = () => {
    this.setState({ showUploadPhotoModal: true });
  };

  showUploadLogoModal = () => {
    this.setState({ showUploadLogoModal: true });
  };

  showDeletePhotoModal = () => {
    this.setState({ showDeletePhotoModal: true });
  };

  showDeleteLogoModal = () => {
    this.setState({ showDeleteLogoModal: true });
  };

  onSave = (info: EditableUserInfos) =>
    this.props.onSave(info).then(() => {
      notification.success(formatMessage('Onboarding.Success'));
    });

  onConfirmDeletePhoto = async () => {
    await this.props.onSave({ imgUrl: null });
    notification.success(formatMessage('Onboarding.ProfilePicture.DeleteModal.DeleteSuccess'));
    this.hideDeletePhotoModal();
  };

  onConfirmDeleteLogo = async () => {
    await this.props.onSave({ 'coBranding.logoUrl': null } as EditableUserInfos);
    notification.success(formatMessage('Onboarding.CompanyLogo.DeleteModal.DeleteSuccess'));
    this.hideDeleteLogoModal();
  };

  render() {
    const { user } = this.props;
    const { showUploadPhotoModal, showDeletePhotoModal, showUploadLogoModal, showDeleteLogoModal } = this.state;

    return (
      <div className="user-onboarding">
        <FormattedMessage tagName="h1" id="Onboarding.AddUserInfo" />
        <div className="top-img-container d-flex">
          <div className="onboarding-photo d-flex">
            <UploadImageIcon onClick={this.showUploadPhotoModal} imgUrl={user.imgUrl} />
            <div className="onboarding-photo-text">
              <FormattedMessage tagName="h3" id="Onboarding.ProfilePicture.Title" />
              <FormattedMessage tagName="label" id="Onboarding.ProfilePicture.Description" />
              <div className="actions">
                {!user.imgUrl && (
                  <Button onClick={this.showUploadPhotoModal} className="upload">
                    <FormattedMessage id="Onboarding.ProfilePicture.Upload" />
                  </Button>
                )}
                {user.imgUrl && (
                  <>
                    <Button onClick={this.showUploadPhotoModal}>
                      <FormattedMessage id="Onboarding.ProfilePicture.Edit" />
                    </Button>
                    <Button onClick={this.showDeletePhotoModal}>
                      <FormattedMessage id="Onboarding.ProfilePicture.Delete" />
                    </Button>
                  </>
                )}
              </div>
            </div>
          </div>
          {userHasPermission(user, PERMISSIONS.PROPOSAL_COBRANDING) && (
            <div className="onboarding-company-logo d-flex">
              <UploadImageIcon onClick={this.showUploadLogoModal} imgUrl={user.coBranding?.logoUrl} />
              <div className="onboarding-photo-text">
                <FormattedMessage tagName="h3" id="Onboarding.CompanyLogo.Title" />
                <FormattedMessage tagName="label" id="Onboarding.CompanyLogo.Description" />
                <div className="actions">
                  {!user.coBranding?.logoUrl && (
                    <Button onClick={this.showUploadLogoModal} className="upload">
                      <FormattedMessage id="Onboarding.CompanyLogo.Upload" />
                    </Button>
                  )}
                  {user.coBranding?.logoUrl && (
                    <>
                      <Button onClick={this.showUploadLogoModal}>
                        <FormattedMessage id="Onboarding.CompanyLogo.Edit" />
                      </Button>
                      <Button onClick={this.showDeleteLogoModal}>
                        <FormattedMessage id="Onboarding.CompanyLogo.Delete" />
                      </Button>
                    </>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        <UserForm user={user} onSubmit={this.onSave} />

        {showUploadPhotoModal && <UploadPhotoModal titleId="UploadPhotoModal.Title" show onCancel={this.hideUploadPhotoModal} />}

        {showUploadLogoModal && <UploadLogoModal titleId="UploadCompanyLogoModal.Title" show onCancel={this.hideUploadLogoModal} />}

        {showDeletePhotoModal && (
          <DestructiveModal
            open
            onOpenChange={open => !open && this.hideDeletePhotoModal()}
            title={formatMessage('Onboarding.ProfilePicture.DeleteModal.Title')}
            content={formatMessage('Onboarding.ProfilePicture.DeleteModal.Body')}
            confirmButtonText={formatMessage('Onboarding.ProfilePicture.DeleteModal.ConfirmButton')}
            cancelButtonText={formatMessage('Common.Cancel')}
            onCancel={this.hideDeletePhotoModal}
            onConfirm={this.onConfirmDeletePhoto}
          />
        )}

        {showDeleteLogoModal && (
          <DestructiveModal
            open
            onOpenChange={open => !open && this.hideDeleteLogoModal()}
            title={formatMessage('Onboarding.CompanyLogo.DeleteModal.Title')}
            content={formatMessage('Onboarding.CompanyLogo.DeleteModal.Body')}
            confirmButtonText={formatMessage('Onboarding.CompanyLogo.DeleteModal.ConfirmButton')}
            cancelButtonText={formatMessage('Common.Cancel')}
            onCancel={this.hideDeleteLogoModal}
            onConfirm={this.onConfirmDeleteLogo}
          />
        )}
      </div>
    );
  }
}

export default UserOnboarding;
